/**
 * `<FieldRef>` — pick a column as a base / frequency / offset triple.
 *
 * A field reference is not a name. `rs`, `wRs` and `mRs` are the same measure at
 * three timeframes, and `ColumnMeta.baseField` already exists to say so — per its
 * own docstring, "so columns that are the same measure at different timeframes
 * can be recognised as related". That is exactly the pivot a builder needs, so
 * this is a view over discovered metadata rather than new machinery.
 *
 * Picking base `rs` then offers only the frequencies that actually exist for it,
 * which is what stops a builder from composing a field id that resolves to
 * nothing.
 *
 * Vocabulary is deliberately shared, not reinvented: the frequency axis uses
 * `TimeframeHeader`'s chips (`W`, `2W`, `15MI`) and the base list uses
 * `ColumnPicker`'s search over field/label/description, so a column is findable
 * by what it means here too.
 */

import { useLayoutEffect, useMemo, useRef, useState } from "react";
import { formatTimeframe, type ScreenerColumnMeta } from "@qufolio/transport";

import { Badge } from "./ColumnPicker.js";

export interface FieldRefValue {
  /** The resolved column id — what the AST stores. */
  field: string;
  base: string;
  /** Canonical `[count]unit`, or `""` for the screen's own timeframe. */
  frequency: string;
  offset: number;
}

export interface FieldRefProps {
  value: FieldRefValue | undefined;
  meta: readonly ScreenerColumnMeta[];
  onChange: (value: FieldRefValue) => void;
  /** Offsets need history the client doesn't have; hidden until §3 lands. */
  allowOffset?: boolean;
}

/** base -> the frequencies it exists at, each resolving to a real field id. */
export interface BaseGroup {
  base: string;
  label: string;
  description: string;
  /** `""` (screen timeframe) first, then by canonical prefix. */
  frequencies: { frequency: string; field: string }[];
}

/**
 * Pivot discovered metadata into base × frequency.
 *
 * Only fields the server described can appear, so a base is always resolvable to
 * a real column — the builder cannot compose a reference to something absent.
 */
export function pivotByBase(meta: readonly ScreenerColumnMeta[]): BaseGroup[] {
  const groups = new Map<string, BaseGroup>();

  for (const m of meta) {
    const base = m.baseField || m.field;
    let group = groups.get(base);
    if (!group) {
      group = { base, label: base, description: "", frequencies: [] };
      groups.set(base, group);
    }
    const frequency = m.timeframe ? formatTimeframe(m.timeframe) : "";
    group.frequencies.push({ frequency, field: m.field });

    // Prefer the screen-timeframe variant's wording for the group as a whole:
    // it is the one a user thinks of as "the" column.
    if (frequency === "" || !group.description) {
      if (m.label) group.label = m.label;
      if (m.description) group.description = m.description;
    }
  }

  for (const group of groups.values()) {
    group.frequencies.sort((a, b) =>
      a.frequency === b.frequency ? 0 : a.frequency === "" ? -1 : b.frequency === "" ? 1 : a.frequency.localeCompare(b.frequency),
    );
  }
  return [...groups.values()].sort((a, b) => a.label.localeCompare(b.label));
}

/**
 * Search by everything a column is called — matching `ColumnPicker` exactly.
 *
 * All four, and each earns its place:
 *
 * - the **base**, so `rs` finds the whole family;
 * - every **field id** in the group, because `wBongoUp` is what a user sees in
 *   the grid and searching the base alone could never find it;
 * - the **header**, which is the name they gave it — a renamed column that
 *   cannot be found by its new name is worse than one with no name at all;
 * - the **description**, so "momentum" finds `jforce2`.
 *
 * The three pickers in this package must agree. They previously did not, and a
 * column findable in the quick picker was missing from the filter editor, which
 * reads as the column not existing rather than as search being narrower here.
 */
/** The bases a picker offers: every one but a script-only field's. */
export function pickableBases(meta: readonly ScreenerColumnMeta[]): BaseGroup[] {
  return pivotByBase(meta.filter((m) => !m.scriptOnly));
}

export function searchBases(groups: readonly BaseGroup[], query: string): BaseGroup[] {
  const needle = query.trim().toLowerCase();
  if (!needle) return [...groups];
  return groups.filter(
    (g) =>
      g.base.toLowerCase().includes(needle) ||
      g.label.toLowerCase().includes(needle) ||
      g.description.toLowerCase().includes(needle) ||
      g.frequencies.some((f) => f.field.toLowerCase().includes(needle)),
  );
}

const POPOVER_WIDTH = 280;
const POPOVER_MAX_HEIGHT = 280;

export function FieldRef({ value, meta, onChange, allowOffset = false }: FieldRefProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const anchorRef = useRef<HTMLButtonElement>(null);
  const [at, setAt] = useState<{ top: number; left: number } | null>(null);

  /**
   * Place the popover from the button's own rect, and flip it above when there
   * is no room below.
   *
   * Measured rather than offset in CSS because the list is `position: fixed` —
   * which is what lets it escape the tab's `overflow: auto`. As an absolutely
   * positioned child it was clipped by that scroll container, so in a short
   * panel the options were simply invisible.
   */
  useLayoutEffect(() => {
    if (!open) return;
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const below = window.innerHeight - rect.bottom;
    const flip = below < POPOVER_MAX_HEIGHT && rect.top > below;
    setAt({
      top: flip ? Math.max(4, rect.top - POPOVER_MAX_HEIGHT - 4) : rect.bottom + 4,
      left: Math.min(Math.max(4, rect.left), window.innerWidth - POPOVER_WIDTH - 4),
    });
  }, [open]);

  const groups = useMemo(() => pivotByBase(meta), [meta]);
  // A script-only field is never offered, but one already chosen still names
  // itself: `current` reads every group, the list only the pickable ones.
  const pickable = useMemo(() => pickableBases(meta), [meta]);
  const matches = useMemo(() => searchBases(pickable, query), [pickable, query]);
  const current = useMemo(
    () => groups.find((g) => g.base === value?.base),
    [groups, value?.base],
  );

  const pickBase = (group: BaseGroup) => {
    // Default to the screen's own timeframe, which is the first entry.
    const first = group.frequencies[0]!;
    onChange({ field: first.field, base: group.base, frequency: first.frequency, offset: 0 });
    setOpen(false);
    setQuery("");
  };

  const pickFrequency = (frequency: string) => {
    const match = current?.frequencies.find((f) => f.frequency === frequency);
    if (!match || !value) return;
    onChange({ ...value, field: match.field, frequency });
  };

  return (
    <span style={{ position: "relative", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
      {/*
        The **id** is what the chip shows, with the header beside it. They are
        different things and conflating them is how a column comes to be known
        by a name no expression can use: `computed1` is what a filter stores and
        what DSL references, while "My ratio" is only how it reads. Showing the
        header alone left a user unable to find the column they had just named.
      */}
      <button
        ref={anchorRef}
        onClick={() => setOpen((o) => !o)}
        style={chipButton}
        title={current?.description}
      >
        {value?.field ? (
          <>
            <code style={idStyle}>{value.field}</code>
            {current?.label && current.label !== value.field && (
              <span style={{ color: "var(--qf-text-muted)" }}> {current.label}</span>
            )}
          </>
        ) : (
          "Pick a column…"
        )}
      </button>

      {/* Only shown when the measure exists at more than one timeframe — a
          single-frequency column would offer a control with one option. */}
      {current && current.frequencies.length > 1 && (
        <span style={{ display: "inline-flex", gap: "0.15rem" }}>
          {current.frequencies.map((f) => (
            <button
              key={f.frequency || "native"}
              onClick={() => pickFrequency(f.frequency)}
              title={f.field}
              style={{
                ...chipButton,
                padding: "0 0.3rem",
                background: f.frequency === value?.frequency ? "var(--qf-selection)" : "transparent",
              }}
            >
              {f.frequency ? f.frequency.toUpperCase() : "—"}
            </button>
          ))}
        </span>
      )}

      {allowOffset && value && (
        <input
          type="number"
          min={0}
          value={value.offset}
          onChange={(e) => onChange({ ...value, offset: Math.max(0, Number(e.target.value) || 0) })}
          title="Periods back"
          style={{ width: 44, ...inputStyle }}
        />
      )}

      {open && (
        <div style={{ ...popover, ...(at ?? { top: 0, left: 0, visibility: "hidden" }) }}>
          <input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search columns…"
            style={{ ...inputStyle, width: "100%", marginBottom: "0.3rem" }}
          />
          <div style={{ maxHeight: POPOVER_MAX_HEIGHT - 60, overflowY: "auto" }}>
            {matches.length === 0 && (
              <div style={{ padding: "0.5rem", color: "var(--qf-text-muted)" }}>No match.</div>
            )}
            {matches.map((group) => (
              <button
                key={group.base}
                onClick={() => pickBase(group)}
                title={group.description}
                style={{
                  display: "flex",
                  width: "100%",
                  alignItems: "center",
                  gap: "0.35rem",
                  padding: "0.25rem 0.4rem",
                  border: "none",
                  background: "transparent",
                  color: "var(--qf-text)",
                  cursor: "pointer",
                  textAlign: "left",
                  fontSize: "0.78rem",
                }}
              >
                <code style={idStyle}>{group.base}</code>
                <span
                  style={{
                    flex: 1,
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    color: "var(--qf-text-muted)",
                  }}
                >
                  {group.label === group.base ? "" : group.label}
                </span>
                {group.frequencies
                  .filter((f) => f.frequency)
                  .map((f) => (
                    <Badge key={f.frequency}>{f.frequency.toUpperCase()}</Badge>
                  ))}
              </button>
            ))}
          </div>
        </div>
      )}
    </span>
  );
}

const chipButton: React.CSSProperties = {
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  padding: "0.1rem 0.4rem",
  background: "transparent",
  color: "var(--qf-text)",
  cursor: "pointer",
  fontSize: "0.76rem",
  whiteSpace: "nowrap",
};

const inputStyle: React.CSSProperties = {
  padding: "0.15rem 0.3rem",
  background: "var(--qf-background)",
  border: "1px solid var(--qf-border)",
  borderRadius: 4,
  color: "var(--qf-text)",
  fontSize: "0.76rem",
};

const idStyle: React.CSSProperties = {
  fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: "0.72rem",
};

const popover: React.CSSProperties = {
  // `fixed`, positioned from the button's own rect, so the list escapes any
  // scrolling ancestor. As `absolute` it was clipped by the tab's
  // `overflow: auto` — in a short panel the options simply were not visible,
  // which reads as the picker being broken rather than merely cut off.
  position: "fixed",
  zIndex: 30,
  width: POPOVER_WIDTH,
  padding: "0.35rem",
  background: "var(--qf-surface-alt)",
  border: "1px solid var(--qf-border)",
  borderRadius: 6,
  boxShadow: "0 8px 24px rgba(0,0,0,0.45)",
};
